import { NextSeo } from "next-seo";
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from 'context/AuthContext';
import * as Sentry from '@sentry/nextjs';
import Cookie from 'js-cookie';
import { AUTH_COOKIE_OPTS } from './authCookieOptions';

import { Tab, Transition } from '@headlessui/react';
import Button from '@ui/Button';

import EmailPasswordSignupForm from "./EmailPasswordSignupForm";
import ProfileSetupForm from "./ProfileSetupForm";
import EmailPasswordLoginForm from "./EmailPasswordLoginForn";
import PasswordlessAuthForm from "./PasswordlessAuthForm";
import { bumpForwardCap, DEFAULT_FORWARD_DEST, resolveAuthLandingAction, resolveForwardDest } from "./resolveAuthLandingAction";


// DEFAULT_FORWARD_DEST moved to resolveAuthLandingAction.ts (GB #1463) — the
// middleware edge forward shares it so both layers land users the same place.

const AuthenticationForm = () => {
  const { user, userDoc, isLoadingUserDoc, userDocError } = useAuth();
  const router = useRouter();

  const [tabIndex, setTabIndex] = useState<number>(0);
  const [userInfo, setUserInfo] = useState({
    uid: '',
    firstName: '',
    lastName: '',
    isSocial: false,
  });

  const [isReady, setIsReady] = useState(false);
  const [message, setMessage] = useState("");
  // GB #1534 Phase 2: terminal state after the user-doc listener's retry
  // budget is exhausted with no doc ever delivered. Replaces the Tab-0
  // spinner (which would otherwise show forever) with a manual-retry state.
  const [hasUserDocError, setHasUserDocError] = useState(false);

  // GB #1435 (D4): guards the authed-forward against re-firing. Once we've
  // kicked off router.replace, this stays true so a re-render during the async
  // token refresh (or navigation) can't launch a second forward — and so the
  // decision core returns 'wait' instead of flashing the login form.
  const hasForwardedRef = useRef(false);

  useEffect(() => {
    const run = async () => {
      try {
        // /login and /signup are statically optimized (no getServerSideProps),
        // so router.query is {} until hydration flips router.isReady. Deciding
        // before that would silently drop a deep-link ?redirect= (QA review,
        // GB #1435). Keep the spinner until the query is real.
        if (!router.isReady) {
          return;
        }
        // Firebase Auth: undefined = unresolved (hard load), null = guest, else authed.
        const authState =
          user === undefined ? 'pending' : user === null ? 'guest' : 'authed';
        const action = resolveAuthLandingAction({
          authState,
          isLoadingUserDoc,
          userDocError,
          hasUsername: !!userDoc?.username,
          pathname: router.pathname,
          tabIndex,
          alreadyForwarded: hasForwardedRef.current,
        });

        // Every branch below reaches a decision other than 'error' — clear
        // any previously-shown terminal state so it can't linger (e.g. a
        // delayed background resubscribe eventually delivers the doc).
        if (action !== 'error') {
          setHasUserDocError(false);
        }

        switch (action) {
          case 'wait':
            // Keep the Tab-0 spinner up (userDoc loading, or a forward already
            // in flight). Never reveal the login form here.
            return;
          case 'error':
            // GB #1534 Phase 2: retry budget exhausted, doc never arrived.
            // isLoadingUserDoc never resolves on its own here — stop waiting
            // and show a manual-retry state instead of spinning forever.
            //
            // qa-review F2: also force isReady false. Without this, a
            // guest→authenticated transition within one mount (a passive
            // cross-tab login, or the iOS Safari transient-null-then-restore
            // case documented near previousUserRef in AuthContext.tsx) can
            // have already latched isReady=true on the guest login-tab
            // BEFORE auth resolves and the retry budget exhausts — leaving
            // Tab.Group's selectedIndex pinned on that tab forever, since it
            // only falls back to Tab 0 while isReady is false. Proved with a
            // scenario probe (guest branch runs first, then authed+error)
            // before merge.
            setIsReady(false);
            setHasUserDocError(true);
            return;
          case 'respect-manual':
            // User picked signup/passwordless/profile-setup — don't override it.
            setIsReady(true);
            return;
          case 'login-tab':
            // Show the password login form. The login form redirects after a
            // successful login.
            setTabIndex(2);
            setIsReady(true);
            return;
          case 'profile-setup':
            // Ghost account (authed, no username) — needs Tab 4. GB #971.
            setTabIndex(4);
            setIsReady(true);
            return;
          case 'forward': {
            // Fully-authenticated user landed on /login or /signup — forward
            // them into the app instead of making them re-enter credentials.
            //
            // Mark forwarded FIRST (synchronously) so a re-render during the
            // async token refresh below can't kick off a second forward.
            hasForwardedRef.current = true;
            // Defensive: the decision core only returns 'forward' when hasUser
            // is true, so this is unreachable — it narrows `user` for the token
            // refresh below without a non-null assertion, and never strands the
            // user (the login-tab fallback still shows a usable form).
            if (!user) {
              setTabIndex(2);
              setIsReady(true);
              return;
            }
            // Cross-navigation loop cap: if middleware keeps bouncing the
            // destination back here (auth backend degraded), fall back to the
            // login form instead of a redirect storm. Per-tab, self-expiring.
            if (bumpForwardCap().capped) {
              console.warn(
                '[auth-lifecycle] authed-forward-loop-cap',
                JSON.stringify({ from: router.pathname })
              );
              setTabIndex(2);
              setIsReady(true);
              return;
            }
            const dest = resolveForwardDest(router.query.redirect, DEFAULT_FORWARD_DEST);
            try {
              // Refresh the auth cookie so the destination's middleware / API
              // auth check sees a fresh token.
              //
              // Client-side router.replace below is DELIBERATE, not a drift
              // from the Dec 2025 "full page navigation after login" decision:
              // that constraint exists so middleware can validate a BRAND-NEW
              // session's cookie on a full document request after a login
              // submit. This forward serves an ALREADY-authenticated session.
              // NOTE: middleware DOES still run on the client-nav /_next/data
              // request (the matcher only excludes _next/static and
              // _next/image, and Next normalizes the data URL back to the
              // page path). The forward works because we Cookie.set a fresh
              // token synchronously BEFORE router.replace, so that middleware
              // check passes — do NOT remove the cookie refresh above; it is
              // load-bearing, and bumpForwardCap is the fallback if the
              // middleware bounce ever recurs. A full reload here would
              // reintroduce the exact flicker this feature removes.
              // E2E-verified (R5 rings, #1449).
              const token = await user.getIdToken();
              Cookie.set('auth', token, AUTH_COOKIE_OPTS);
              console.warn(
                '[auth-lifecycle] authed-forward',
                JSON.stringify({ from: router.pathname, to: dest })
              );
              Sentry.addBreadcrumb({
                category: 'auth-lifecycle',
                message: 'authed-forward',
                level: 'info',
                data: { from: router.pathname, to: dest },
              });
              // Leave isReady FALSE: the Tab-0 spinner stays up through the
              // client-side navigation, never flashing the login form.
              router.replace(dest);
            } catch (err) {
              // Loop-breaker (Dec 2025 infinite-spinner incident in this exact
              // component): if the token refresh fails, fall back to the
              // original behavior rather than stranding the user on an endless
              // spinner. hasForwardedRef stays true so we don't retry.
              console.error(
                '[auth-lifecycle] authed-forward failed; falling back to login form',
                err
              );
              setTabIndex(2);
              setIsReady(true);
            }
            return;
          }
          default: {
            // Compile-time exhaustiveness guard: if a new AuthLandingAction is
            // added and not handled above, this line fails to type-check.
            const _exhaustive: never = action;
            return _exhaustive;
          }
        }
      } catch (err) {
        // Final safety net — never leave the form stuck on the spinner.
        setMessage("");
        console.error(err);
        setIsReady(true);
      }
    };

    run();
    // Deps are the auth-state inputs plus router.isReady (the effect must
    // re-fire once hydration populates router.query — see the isReady gate
    // above). tabIndex and the rest of router are read but omitted so the
    // effect re-runs on auth-state changes, not on every tab click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userDoc, isLoadingUserDoc, userDocError, router.isReady]);

  return (
    <>
      <NextSeo
        title="Sign Up for GearFocus - Buy and Sell Used Camera Gear"
        description="Join GearFocus to buy and sell used camera gear easily. Create your account today and access a marketplace designed for photographers and creators."
        noindex={true}
        nofollow={true}
      />

      <div className="mt-[2.4rem] flex w-full items-center justify-center px-[2.4rem] pb-[3.2rem] sm:p-0 lg:mt-[3rem]
      ">
        <Tab.Group selectedIndex={isReady ? tabIndex : 0} onChange={setTabIndex}>
          <Tab.List className="hidden">
            <Tab />
            <Tab />
            <Tab />
            <Tab />
            <Tab />
          </Tab.List>
          <Tab.Panels>
            {/* Step 0: Spinner, or GB #1534 Phase 2's terminal error state */}
            <Tab.Panel>
              {hasUserDocError ? (
                <div
                  role="alert"
                  className="flex min-h-[40vh] w-full items-center justify-center px-[2.4rem]"
                >
                  <div className="text-center max-w-[36rem]">
                    <p className="text-[1.4rem] font-medium mb-6">
                      We&apos;re having trouble connecting to your account.
                    </p>
                    <p className="text-[1.4rem] mb-6 text-gray-600">
                      If this continues, contact{' '}
                      <a href="mailto:support@gearfocus.com" className="underline font-semibold">
                        support@gearfocus.com
                      </a>
                      .
                    </p>
                    <Button
                      text="Try Again"
                      type="primary"
                      onClick={() => window.location.reload()}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[40vh] w-full items-center justify-center">
                  <div className="text-center text-[1.4rem] font-medium">
                    {message}
                    <div className="mt-[1.2rem] animate-spin rounded-full border-4 border-gray-300 border-t-brand-secondary h-10 w-10 mx-auto" />
                  </div>
                </div>
              )}
            </Tab.Panel>

            {/* Step 1: Passwordless Auth */}
            <Tab.Panel>
              <Transition appear show={isReady}>
                <PasswordlessAuthForm
                  switchToPasswordTab={() => setTabIndex(2)}
                  switchToSignupTab={() => setTabIndex(3)}
                  continueFn={(uid, firstName, lastName, isSocial) => {
                    setUserInfo({ uid, firstName, lastName, isSocial });
                    setTabIndex(4);
                  }}
                />
              </Transition>
            </Tab.Panel>

            {/* Step 2: Password Login */}
            <Tab.Panel>
              <Transition appear show={isReady}>
                <EmailPasswordLoginForm
                  switchToPasswordlessTab={() => setTabIndex(1)}
                  switchToSignupTab={() => setTabIndex(3)}
                />
              </Transition>
            </Tab.Panel>

            {/* Step 3: Signup */}
            <Tab.Panel>
              <Transition appear show={isReady}>
                <EmailPasswordSignupForm
                  switchToPasswordTab={() => setTabIndex(2)}
                  continueFn={(uid, firstName, lastName, isSocial) => {
                    setUserInfo({ uid, firstName, lastName, isSocial });
                    setTabIndex(4);
                  }}
                />
              </Transition>
            </Tab.Panel>

            {/* Step 4: Profile Setup */}
            <Tab.Panel>
              <Transition appear show={isReady && tabIndex===4}>
                {userInfo.uid ? (
                  <ProfileSetupForm {...userInfo} />
                ) : (
                  <div className="flex min-h-[40vh] w-full items-center justify-center">
                    <div className="text-center text-[1.4rem] font-medium">
                      Loading profile setup...
                      <div className="mt-[1.2rem] animate-spin rounded-full border-4 border-gray-300 border-t-brand-secondary h-10 w-10 mx-auto" />
                    </div>
                  </div>
                )}
              </Transition>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </>
  );
};

export default AuthenticationForm;
